import React from 'react'
import CardChart from '../Components/CardChart'
import useFetchData from '../../../../../../hooks/useFetchData'
import {
  Box,
  Typography,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Chip,
  Skeleton
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'

const MyClasses = ({ userId, isLoading, myClasses }) => {
  const years = useSelector((state) => state?.tenant?.years || [])

  return (
    <CardChart title='My Classes' bgColor='#fff' px={0} py={0}>
      <TableContainer>
        <Table size='small' aria-label='my classes table'>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell width={'220px'} sx={{ px: 3 }}>
                    <Typography color='textSecondary' className='capitalize'>
                      <Skeleton width={120} height={20} />
                    </Typography>
                    <Typography color='textSecondary' className='capitalize'>
                      <Skeleton width={100} height={40} />
                    </Typography>
                    <Typography color='textSecondary' className='capitalize'>
                      <Skeleton width={120} height={20} />
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='body2' color='textPrimary'>
                      <Skeleton width={100} height={20} />
                    </Typography>
                    <Typography variant='body2' color='textPrimary'>
                      <Skeleton width={100} height={20} />
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='body2' color='textPrimary'>
                      <Skeleton width={200} height={20} />
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='body2' color='textPrimary'>
                      <Skeleton width={100} height={20} />
                    </Typography>
                    <Typography variant='body2' color='textPrimary'>
                      <Skeleton width={100} height={20} />
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            ) : myClasses?.length > 0 ? (
              myClasses?.map((item, index) => {
                const className = item?.code || item?.name || `Class ${index + 1}`
                const studentCount = item?.students?.length || 0
                const coverage = Number(item?.coverage ?? 0)
                const pendingMarkings = item?.pendingSkillsToMark ?? 0
                const markedSkills = item?.markedSkills ?? 0
                const yearLevel = years
                  .filter((year) => item?.yearLevel?.includes(year._id))
                  .map((year) => year.name)
                  .join(', ')
                const activeUnitsCount = item?.activeUnitsCount || 0
                const inactiveUnitsCount = item?.inactiveUnitsCount || 0

                return (
                  <TableRow key={className + index} hover>
                    <TableCell sx={{ color: '#111827', fontWeight: 600, py: 1.5 }}>
                      <Box
                        sx={{
                          display: 'flex-column',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1,
                          px: 3
                        }}
                      >
                        <Typography variant='body2' sx={{ color: '#6b7280' }}>
                          {yearLevel || 'N/A'}
                        </Typography>
                        <Typography variant='h5' sx={{ color: '#111827', fontWeight: 600 }}>
                          {className}
                        </Typography>
                        <Typography variant='body2' sx={{ color: '#6b7280' }}>
                          {item?.subject?.name || item?.course || 'N/A'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, py: 1.5 }}>
                        <Chip
                          label={`${activeUnitsCount} Active`}
                          variant='outlined'
                          style={{
                            color: 'var(--accent-foreground)',
                            backgroundColor: 'var(--accent)',
                            maxWidth: '120px'
                          }}
                          size='small'
                          className='font-bold'
                        />
                        <Chip
                          label={`${inactiveUnitsCount} Inactive`}
                          style={{
                            color: 'var(--muted-foreground)',
                            backgroundColor: 'var(--muted)',
                            maxWidth: '120px'
                          }}
                          size='small'
                          className='font-bold'
                        />
                      </Box>
                    </TableCell>
                    <TableCell align='right' sx={{ color: '#6b7280', py: 1.5 }}>
                      {studentCount} students
                    </TableCell>

                    <TableCell sx={{ py: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ flexGrow: 1, minWidth: 100 }}>
                          <LinearProgress
                            variant='determinate'
                            value={Math.min(Math.max(coverage, 0), 100)}
                            sx={{
                              height: 15,
                              borderRadius: 1,
                              backgroundColor: 'var(--muted)',
                              '& .MuiLinearProgress-bar': {
                                borderRadius: 1,
                                backgroundColor: 'var(--primary)'
                              }
                            }}
                          />
                        </Box>
                        <Typography variant='body2' sx={{ color: '#111827', fontWeight: 600, minWidth: 36 }}>
                          {Math.round(coverage)}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align='right' sx={{ color: '#6b7280', py: 1.5 }}>
                      <Typography variant='body2' sx={{ color: 'var(--primary)', fontWeight: 600 }}>
                        {markedSkills} marked skills by students
                      </Typography>
                      <Typography variant='body2' sx={{ color: 'var(--muted-foreground)', fontWeight: 600 }}>
                        {pendingMarkings} pending skills by students
                      </Typography>
                    </TableCell>
                    <TableCell align='right' sx={{ color: '#6b7280', py: 1.5, px: 3 }}>
                      <Link
                        to={`/dashboard/classes/${item.curriculumId}/preview/${item._id}`}
                        style={{ textDecoration: 'none', color: '#3b82f6' }}
                      >
                        <FontAwesomeIcon icon='fa-solid fa-chevron-right' />
                      </Link>
                    </TableCell>
                  </TableRow>
                )
              })
            ) : (
              <TableRow>
                <TableCell colSpan={4} align='center' sx={{ py: 3, color: '#6b7280' }}>
                  {"You don't have any classes yet"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </CardChart>
  )
}

export default MyClasses
